import React from 'react';
import classnames from 'classnames';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import Add from '@mui/icons-material/Add';
import VisibilityOff from '@mui/icons-material/VisibilityOffOutlined';

import { Button } from 'uikit/buttons';
import { Menu } from 'uikit/menus';

import { EmptyState, SectionHeader } from '@/exceed';
import { NestedRows } from '@/nestedRows';

import AiGenerateButton from './aiGenerateButton';
import CourseOutlineLoading from './courseOutlineLoading';

import getLearningObjectivesMenuItems from '../helpers/getLearningObjectivesMenuItems';

import styles from '../styles/learningObjectives.module.scss';

VisibilityOff.display = 'LearningObjectivesHidden';

const LearningObjectivesList = ({
  glow = [],
  isGenerating = false,
  isLearningObjectivesDisplayed = false,
  isAiGenerateButtonDisabled = false,
  isReadOnly = false,
  handleActionItemClick = () => {},
  handleAiGenerateClick = () => {},
  orderedLearningObjectives = {},
  renderCustomPlaceholder = () => {},
  renderListItem = () => {},
  renderTypingEffect = () => {},
  reorderLearningObjectives = () => {},
  shouldShowEmptyState = false,
  shouldShowAiGenerateButton = false,
  shouldShowNestedRows = false,
  shouldShowLoadingState = true,
}) => {
  const addMenuItems = getLearningObjectivesMenuItems({ add: () => handleActionItemClick('add') });
  const toggleVisibilityButton = isLearningObjectivesDisplayed ? 'hide' : 'show';
  const moreMenuItems = getLearningObjectivesMenuItems({
    [toggleVisibilityButton]: () => handleActionItemClick(toggleVisibilityButton),
  });

  const emptyStateActions = [
    <Menu
      component={
        <Button
          className={styles.button}
          icon={<Add />}
          text={i18n.t('learning-objectives.empty-state-button')}
          variant="outlined"
        />
      }
      contentClassName={styles.menu}
      horizontalPosition="center"
      key="add-learning-objective-menu"
      verticalPosition="auto"
    >
      {addMenuItems}
    </Menu>,
  ];

  if (shouldShowAiGenerateButton) {
    emptyStateActions.push(
      <AiGenerateButton
        className={styles.button}
        content={i18n.t('learning-objectives.suggest-objectives')}
        disabledText={i18n.t('source-material-processing')}
        isDisabled={isAiGenerateButtonDisabled}
        isGenerating={isGenerating}
        key="ai-generate-button"
        onClick={handleAiGenerateClick}
      />,
    );
  }

  const rightColumn =
    !isLearningObjectivesDisplayed && !isReadOnly ? (
      <VisibilityOff sx={{ height: 18, width: 18 }} />
    ) : null;

  return (
    <div className={classnames(styles.element, ...glow)} data-testid="learning-objectives">
      <SectionHeader
        addMenuItems={addMenuItems}
        className={{ [styles.noBorder]: !shouldShowNestedRows }}
        moreMenuItems={moreMenuItems}
        rightColumnChildren={rightColumn}
        shouldShowAddMenu={!isReadOnly}
        shouldShowMoreMenu={!isReadOnly}
        title={i18n.t('learning-objectives.title')}
        testId="learning-objectives-title"
      />
      {shouldShowNestedRows && (
        <NestedRows
          className={styles.list}
          isDragEnabled={!isReadOnly}
          onDragEnd={(args) => reorderLearningObjectives({ ...args })}
          data={orderedLearningObjectives}
          shouldPreventTransform
          renderComponent={renderListItem}
          renderCustomPlaceholder={renderCustomPlaceholder}
          containerHeight="auto"
          shouldExpandOnNewChildren
          testId="learning-objectives-list"
        />
      )}
      {renderTypingEffect()}
      {shouldShowLoadingState && (
        <CourseOutlineLoading className={styles.loading} testId="learning-objectives-loading" />
      )}
      {shouldShowEmptyState && (
        <EmptyState
          actions={emptyStateActions}
          body={i18n.t('learning-objectives.empty-state-body')}
          testId="learning-objectives-empty-state"
          title={
            isReadOnly
              ? i18n.t('learning-objectives.empty-state-title-read-only')
              : i18n.t('learning-objectives.empty-state-title')
          }
        />
      )}
    </div>
  );
};

LearningObjectivesList.propTypes = {
  glow: PropTypes.arrayOf(PropTypes.string),
  isGenerating: PropTypes.bool,
  isLearningObjectivesDisplayed: PropTypes.bool,
  isAiGenerateButtonDisabled: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  handleActionItemClick: PropTypes.func,
  handleAiGenerateClick: PropTypes.func,
  orderedLearningObjectives: PropTypes.shape({}),
  renderCustomPlaceholder: PropTypes.func,
  renderListItem: PropTypes.func,
  renderTypingEffect: PropTypes.func,
  reorderLearningObjectives: PropTypes.func,
  shouldShowEmptyState: PropTypes.bool,
  shouldShowAiGenerateButton: PropTypes.bool,
  shouldShowNestedRows: PropTypes.bool,
  shouldShowLoadingState: PropTypes.bool,
};

export default LearningObjectivesList;
